import { useCallback, useEffect, useMemo, useState } from "react";
import { normalizePreviewOrigin } from "../Admin/components/charitySetup/onboarding/onboardingCustomation/previewOrigin";

export const PREVIEW_LOAD_TIMEOUT_MS = 12000;
export const PREVIEW_INIT_TIMEOUT_MS = 4000;
export const PREVIEW_FALLBACK_INIT_TIMEOUT_MS = 20000;
export const PREVIEW_PRIMARY_INIT_TIMEOUT_MS = 2500;

export type PreviewLoadState = "loading" | "ready" | "failed";
export type PreviewOriginSource = "primary" | "fallback";

export function getPreviewOrigins(
  whitelabelUrl?: string | null,
  customizationIframeOrigin?: string | null
) {
  const primary = normalizePreviewOrigin(whitelabelUrl);
  const fallback = normalizePreviewOrigin(customizationIframeOrigin);
  return { primary, fallback };
}

export function usePreviewOriginFallback(
  whitelabelUrl?: string | null,
  customizationIframeOrigin?: string | null | undefined,
  fallbackConfigLoaded = true
) {
  const { primary, fallback } = useMemo(
    () => getPreviewOrigins(whitelabelUrl, customizationIframeOrigin),
    [whitelabelUrl, customizationIframeOrigin]
  );

  const previewOriginsResolved =
    fallbackConfigLoaded && Boolean(primary || fallback);

  const [originSource, setOriginSource] = useState<PreviewOriginSource>(() =>
    primary ? "primary" : "fallback"
  );
  const [loadState, setLoadState] = useState<PreviewLoadState>("loading");

  const activeOrigin =
    originSource === "primary" ? primary : fallback;

  useEffect(() => {
    setOriginSource(primary ? "primary" : "fallback");
    if (!fallbackConfigLoaded) {
      setLoadState("loading");
      return;
    }
    setLoadState(primary || fallback ? "loading" : "failed");
  }, [primary, fallback, fallbackConfigLoaded]);

  useEffect(() => {
    if (!previewOriginsResolved) {
      return;
    }
    if (!activeOrigin) {
      setLoadState("failed");
      return;
    }
    setLoadState("loading");
  }, [activeOrigin, originSource, previewOriginsResolved]);

  const handlePreviewReady = useCallback(() => {
    setLoadState("ready");
  }, []);

  const handlePreviewFailure = useCallback(() => {
    if (!previewOriginsResolved) {
      return;
    }
    if (
      originSource === "primary" &&
      fallback &&
      fallback !== primary
    ) {
      setOriginSource("fallback");
      return;
    }
    setLoadState("failed");
  }, [originSource, primary, fallback, previewOriginsResolved]);

  return {
    activeOrigin: previewOriginsResolved ? activeOrigin : undefined,
    originSource,
    loadState: previewOriginsResolved ? loadState : "loading",
    primaryOrigin: primary,
    fallbackOrigin: fallback,
    handlePreviewReady,
    handlePreviewFailure,
    isUsingFallback: originSource === "fallback",
    previewOriginsResolved,
  };
}

export function usePreviewLoadTimeouts(
  iframeOrigin: string | undefined,
  iframeLoaded: boolean,
  previewReady: boolean,
  onFailure: () => void,
  loadTimeoutMs = PREVIEW_LOAD_TIMEOUT_MS,
  initTimeoutMs = PREVIEW_INIT_TIMEOUT_MS
) {
  useEffect(() => {
    if (!iframeOrigin || iframeLoaded) {
      return;
    }
    const timer = setTimeout(onFailure, loadTimeoutMs);
    return () => clearTimeout(timer);
  }, [iframeOrigin, iframeLoaded, onFailure, loadTimeoutMs]);

  useEffect(() => {
    if (!iframeOrigin || !iframeLoaded || previewReady) {
      return;
    }
    const timer = setTimeout(onFailure, initTimeoutMs);
    return () => clearTimeout(timer);
  }, [iframeOrigin, iframeLoaded, previewReady, onFailure, initTimeoutMs]);
}
